import React, { useCallback, useEffect, useRef, useState } from 'react'
import api from 'api/api'
import { currentUserUsesClientSigning } from 'lib/clientSigningFeature'
import {
    claimWelcomeGift,
    ensureLocalWallet,
    loadLocalWalletStatus,
    NotLocalWalletAccountError,
    OPEN_WALLET_SETUP_EVENT,
    recordWalletSetupClosed,
    walletSetupRemaining,
    withTimeout,
    type LocalWalletStatus,
} from 'lib/localWallet'
import { passkeyBrowserUnavailableReason } from 'lib/walletPasskeyWebAuthn'
import {
    consumeWalletSetupPending,
    HELLO_PROMPT_EVENT,
    markHelloPromptPending,
} from 'lib/signupOnboarding'
import useStreamFilter from 'components/filters/useStreamFilters'
import questModel from 'components/quests/questModel'
import { requestChannelJoin } from 'components/teams/ChannelJoinInviteModal'
import { membersOnlyPostReason } from 'state/channelJoinInvite'
import { cacheQuest } from 'state/cache'
import {
    dismissHomeScreenNudge,
    homeScreenNudgeDismissed,
    isIosBrowser,
    isStandaloneApp,
} from 'lib/homeScreenNudge'
import { isRunningInExpoWebView } from 'utils/nativeShare'
import HomeScreenNudge from './HomeScreenNudge'
import WalletSetup from './WalletSetup'
import WalletRestore from './WalletRestore'
import WalletFileCheck from './WalletFileCheck'

export { OPEN_WALLET_SETUP_EVENT }
/** Dispatch to open "Check your wallet file" (notification button, Wallet pill). */
export const OPEN_WALLET_FILE_CHECK_EVENT = 'treechat:open-wallet-file-check'

type Mode = 'hidden' | 'setup' | 'restore' | 'fileCheck'

// The iOS "Add Treechat to your Home Screen" card (HomeScreenNudge) is off for now.
const HOME_SCREEN_NUDGE_ENABLED = false

// Making the wallet takes seconds; past this a request is hung, so offer Try again
// instead of leaving "Making your wallet…" up for good.
const PREPARE_TIMEOUT_MS = 30_000

const RESTORE_DISMISSED_KEY = 'treechat-wallet-restore-dismissed'

function restoreDismissedThisSession(userId: string): boolean {
    try {
        return sessionStorage.getItem(RESTORE_DISMISSED_KEY) === userId
    } catch (_error) {
        return false
    }
}

function dismissRestoreThisSession(userId: string) {
    try {
        sessionStorage.setItem(RESTORE_DISMISSED_KEY, userId)
    } catch (_error) {
        // Without storage the restore screen simply asks again next load.
    }
}

// Browser-wallet accounts: makes the wallet on first load, opens the backup setup
// until it is done or skipped, and asks for a restore on a device without the wallet.
export default function WalletSetupHost() {
    const userId = window.gon?.currentUser?.id as string | undefined
    // The centre panel at the latest render: "Introduce yourself" opens its thread there.
    const { selectNewFilter } = useStreamFilter()
    const selectNewFilterRef = useRef(selectNewFilter)
    selectNewFilterRef.current = selectNewFilter
    // Right after a browser-wallet signup the setup is up from the first render
    // ("Making your wallet…"), so the feed never shows before it.
    const [openedEarly] = useState(
        () => !!userId && currentUserUsesClientSigning() && consumeWalletSetupPending(userId),
    )
    const [mode, setMode] = useState<Mode>(openedEarly ? 'setup' : 'hidden')
    const [status, setStatus] = useState<LocalWalletStatus | null>(null)
    const [homeScreenNudgeHidden, setHomeScreenNudgeHidden] = useState(
        () => !userId || homeScreenNudgeDismissed(userId),
    )
    // From "Introduce yourself" until they post the intro or leave the new-thread
    // screen, the nudge waits ('opening' covers the channel lookup before the draft).
    const [introducing, setIntroducing] = useState<false | 'opening' | 'open'>(false)
    useEffect(() => {
        if (introducing !== 'open') return undefined
        const timer = window.setInterval(() => {
            if (window.location.pathname !== '/new') setIntroducing(false)
        }, 1000)
        return () => window.clearInterval(timer)
    }, [introducing])
    const [error, setError] = useState('')
    const started = useRef(false)
    const awaitingFirstLoad = useRef(openedEarly)

    const prepare = useCallback(
        async (openSetup: boolean) => {
            if (!userId) return
            setError('')
            try {
                const { loaded, result } = await withTimeout(
                    (async () => {
                        const loaded = await loadLocalWalletStatus()
                        return { loaded, result: await ensureLocalWallet(userId, loaded) }
                    })(),
                    PREPARE_TIMEOUT_MS,
                    'Treechat is taking too long to answer. Check your connection and try again.',
                )
                setStatus(result.status)
                // A wallet registered just now: load its address and balance into the app.
                if (!loaded.registered && result.status.registered) {
                    void api.ensureBsvWallet().catch(() => {})
                }
                if (result.state === 'needs_restore') {
                    if (!openSetup && restoreDismissedThisSession(userId)) {
                        if (awaitingFirstLoad.current) setMode('hidden')
                        return
                    }
                    setMode('restore')
                } else {
                    const remaining = walletSetupRemaining(
                        result.status,
                        !passkeyBrowserUnavailableReason(),
                    )
                    const outstanding =
                        remaining.stepsLeft ||
                        (result.status.gift_sats > 0 && remaining.giftUnclaimed)
                    if (openSetup || (outstanding && !result.status.wizard_closed)) setMode('setup')
                    else {
                        // An earned gift that never got paid (e.g. the file check's claim
                        // failed) is claimed again quietly; the claim is safe to repeat.
                        if (result.status.gift_sats > 0 && remaining.giftUnclaimed)
                            void claimWelcomeGift()
                                .then(setStatus)
                                .catch(() => {})
                        // Opened early but nothing is left to do: step aside.
                        if (awaitingFirstLoad.current) setMode('hidden')
                    }
                }
            } catch (caught) {
                // Not a browser-wallet account: never leave a setup screen up for it, even
                // when this answer comes on a retry after a failed first check.
                if (caught instanceof NotLocalWalletAccountError) {
                    setMode('hidden')
                    return
                }
                // Only a setup the person is looking at (just signed up, or opened it) shows
                // the error; a failed background check never blocks the app.
                if (!openSetup && !awaitingFirstLoad.current) {
                    console.warn('Wallet status check failed:', caught)
                    return
                }
                setError(
                    caught instanceof Error ? caught.message : 'Your wallet could not be set up.',
                )
                setMode('setup')
            } finally {
                awaitingFirstLoad.current = false
            }
        },
        [userId],
    )

    useEffect(() => {
        if (started.current || !userId || !currentUserUsesClientSigning()) return
        started.current = true
        void prepare(false)
    }, [prepare, userId])

    useEffect(() => {
        const open = () => void prepare(true)
        const openFileCheck = () => {
            loadLocalWalletStatus()
                .then(next => {
                    setStatus(next)
                    setMode('fileCheck')
                })
                .catch(() => {})
        }
        window.addEventListener(OPEN_WALLET_SETUP_EVENT, open)
        window.addEventListener(OPEN_WALLET_FILE_CHECK_EVENT, openFileCheck)
        return () => {
            window.removeEventListener(OPEN_WALLET_SETUP_EVENT, open)
            window.removeEventListener(OPEN_WALLET_FILE_CHECK_EVENT, openFileCheck)
        }
    }, [prepare])

    // A new thread in Treechat Introductions, the way the New button starts one, with
    // "Introduce yourself" in its composer. A members-only channel asks to join first.
    const openIntroductionThread = useCallback(async (teamId: string) => {
        const filter = { team_ids: [teamId] }
        let team = null
        try {
            team = await api.getTeam(teamId)
        } catch (_error) {
            // Without the channel's settings the draft still opens; posting checks them.
        }
        const open = () => {
            const draft = questModel.newQuestFromFilter(
                // After posting, the intro stays in view: no reply box opens.
                {
                    composer_placeholder: 'Introduce yourself',
                    no_reply_focus_after_publish: true,
                    no_join_invite_after_publish: true,
                },
                filter,
                { team },
            )
            cacheQuest(draft)
            selectNewFilterRef.current(
                { questId: draft.id },
                { newThread: true, draftFilter: filter },
            )
            window.history.pushState({}, '', '/new')
            setIntroducing('open')
        }
        if (team && questModel.needsChannelJoinToPost(team, window.gon?.currentUser)) {
            requestChannelJoin(team, {
                reasonText: membersOnlyPostReason(team.name || 'this channel'),
                afterJoin: async () => open(),
            })
            return
        }
        open()
    }, [])

    const close = useCallback(
        async ({
            askForHelloPost,
            introduce = false,
        }: {
            askForHelloPost: boolean
            introduce?: boolean
        }) => {
            setMode('hidden')
            try {
                setStatus(await recordWalletSetupClosed())
            } catch (_error) {
                // Closing is a preference; the setup reopens next visit if this failed.
            }
            const introductionsTeamId = status?.introductions_team_id
            if (introduce && introductionsTeamId) {
                setIntroducing('opening')
                void openIntroductionThread(introductionsTeamId)
            } else if (askForHelloPost && userId) {
                markHelloPromptPending(userId)
                window.dispatchEvent(new Event(HELLO_PROMPT_EVENT))
            }
        },
        [userId, status, openIntroductionThread],
    )

    if (!userId) return null
    if (mode === 'hidden') {
        // Once the setup is over, iOS browsers are asked to keep Treechat on the Home
        // Screen, where Safari doesn't clear the wallet after a week away.
        const nudge =
            HOME_SCREEN_NUDGE_ENABLED &&
            !!status?.registered &&
            status.wizard_closed &&
            !homeScreenNudgeHidden &&
            !introducing &&
            isIosBrowser() &&
            !isStandaloneApp() &&
            !isRunningInExpoWebView()
        return nudge ? (
            <HomeScreenNudge
                onDismiss={() => {
                    dismissHomeScreenNudge(userId)
                    setHomeScreenNudgeHidden(true)
                }}
            />
        ) : null
    }
    if (mode === 'fileCheck') {
        return (
            <WalletFileCheck
                status={status}
                onStatus={setStatus}
                onClose={() => setMode('hidden')}
            />
        )
    }
    if (mode === 'restore') {
        return (
            <WalletRestore
                userId={userId}
                // A saved wallet file counts here: the wallet can't be replaced once it has one.
                backedUp={!!status?.backed_up || !!status?.backup_file_saved}
                onClose={() => {
                    dismissRestoreThisSession(userId)
                    setMode('hidden')
                }}
                onStartOver={() => void prepare(true)}
                onRestored={() => setMode('hidden')}
            />
        )
    }
    return (
        <WalletSetup
            userId={userId}
            status={status}
            setupError={error}
            onRetrySetup={() => void prepare(true)}
            onStatus={setStatus}
            onClose={close}
        />
    )
}
